import { useMutation, useQuery } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { Download, FileText } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DateFilterField } from "@/components/date-filter-field";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { RecentSignInsTable } from "@/components/report/recent-signins-table";
import { SiteBreakdownTable } from "@/components/report/site-breakdown-table";
import { SummaryCards } from "@/components/summary-cards";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createReportPdf } from "@/lib/create-report-pdf";
import type { SummaryMetric } from "@/lib/report-metrics";
import {
  createSummaryMetrics,
  formatDateLabel,
  numberFormatter,
} from "@/lib/report-metrics";
import type {
  FormsReportPreviewResponse,
  ManagerReportRecentSignIn,
  ManagerReportSiteBreakdownEntry,
} from "@/types/reports";
import { trpc } from "@/utils/trpc";

type ReportTypeOption = {
  id: "attendance" | "forms";
  label: string;
  subtitle?: string;
};

const REPORT_TYPES: readonly ReportTypeOption[] = [
  {
    id: "attendance",
    label: "Attendance overview",
    subtitle: "Sign-ins, coverage and flagged activity",
  },
  {
    id: "forms",
    label: "Forms submissions",
    subtitle: "Export worker-submitted forms by site and date range",
  },
] as const;

type ReportCapabilities = {
  historyWindowDays: number | null;
  canViewAttendanceReports: boolean;
  canExportCsv: boolean;
  canExportPdf: boolean;
  canViewFormsReporting: boolean;
  canExportForms: boolean;
  canReceiveOperationalEmails: boolean;
};

type UpgradeTarget = "base" | "core";

function getHistoryWindowStart(days: number) {
  const now = new Date();
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - (days - 1)
    )
  );
}

function exceedsHistoryWindow(params: {
  historyWindowDays: number | null;
  startDate: Date | undefined;
  endDate: Date | undefined;
}) {
  if (params.historyWindowDays === null) {
    return false;
  }

  if (!params.startDate) {
    return true;
  }

  const earliestDate = getHistoryWindowStart(params.historyWindowDays);

  if (params.startDate < earliestDate) {
    return true;
  }

  if (params.endDate && params.endDate < earliestDate) {
    return true;
  }

  return false;
}

function getHistoryWindowMessage(days: number) {
  return `Free and Base include up to ${days} days of report history. Narrow the date range or upgrade to Core to unlock full history.`;
}

function getUpgradeLabel(target: UpgradeTarget) {
  return target === "base" ? "Upgrade to Base" : "Upgrade to Core";
}

function FiltersSection({
  selectedSiteId,
  onSiteChange,
  sites,
  selectedWorkerId,
  onWorkerChange,
  workers,
  isWorkersLoading,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  reportType,
  onReportTypeChange,
  reportDescription,
  onExportCsv,
  onExportPdf,
  isExportingCsv,
  isExportCsvDisabled,
  isExportPdfDisabled,
  exportCsvLabel,
  exportPdfLabel,
  lockedMessage,
  lockedUpgradeTarget,
  historyWindowDays,
}: {
  selectedSiteId: string;
  onSiteChange: (value: string) => void;
  sites: { id: string; name: string }[];
  selectedWorkerId: string;
  onWorkerChange: (value: string) => void;
  workers: { id: string; fullName: string | null; email: string | null }[];
  isWorkersLoading: boolean;
  startDate: Date | undefined;
  onStartDateChange: (date: Date | undefined) => void;
  endDate: Date | undefined;
  onEndDateChange: (date: Date | undefined) => void;
  reportType: ReportTypeOption["id"];
  onReportTypeChange: (value: ReportTypeOption["id"]) => void;
  reportDescription?: string;
  onExportCsv: () => void;
  onExportPdf: () => void;
  isExportingCsv: boolean;
  isExportCsvDisabled: boolean;
  isExportPdfDisabled: boolean;
  exportCsvLabel: string;
  exportPdfLabel: string;
  lockedMessage: string | null;
  lockedUpgradeTarget: UpgradeTarget | null;
  historyWindowDays: number | null;
}) {
  const earliestAllowedDate =
    historyWindowDays != null
      ? getHistoryWindowStart(historyWindowDays)
      : undefined;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Filter Reports</CardTitle>
        <CardDescription>
          Choose a site, worker, date range and report type to refresh metrics
          and exports.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="report-site-filter"
            className="text-xs font-medium text-muted-foreground"
          >
            Site
          </label>
          <Select value={selectedSiteId} onValueChange={onSiteChange}>
            <SelectTrigger id="report-site-filter" className="h-10 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All managed sites</SelectItem>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="report-worker-filter"
            className="text-xs font-medium text-muted-foreground"
          >
            Worker
          </label>
          <Select
            value={selectedWorkerId}
            onValueChange={onWorkerChange}
            disabled={isWorkersLoading}
          >
            <SelectTrigger
              id="report-worker-filter"
              className="h-10 w-full"
              disabled={isWorkersLoading}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All workers</SelectItem>
              {workers.map((worker) => (
                <SelectItem key={worker.id} value={worker.id}>
                  {worker.fullName ?? worker.email ?? worker.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DateFilterField
          id="report-start-date"
          label="Start date"
          placeholder="Select date"
          date={startDate}
          onChange={onStartDateChange}
          fromDate={earliestAllowedDate}
        />

        <DateFilterField
          id="report-end-date"
          label="End date"
          placeholder="Select date"
          date={endDate}
          onChange={onEndDateChange}
        />

        {historyWindowDays != null ? (
          <p className="text-xs text-muted-foreground">
            Free and Base include {historyWindowDays} days of report history.{" "}
            <Link to="/billing" className="underline underline-offset-2">
              Upgrade to Core
            </Link>{" "}
            for full history.
          </p>
        ) : null}

        <div className="flex flex-col gap-1">
          <label
            htmlFor="report-type"
            className="text-xs font-medium text-muted-foreground"
          >
            Report type
          </label>
          <Select
            value={reportType}
            onValueChange={(value) =>
              onReportTypeChange(value as ReportTypeOption["id"])
            }
          >
            <SelectTrigger id="report-type" className="h-10 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPORT_TYPES.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {reportDescription ? (
            <p className="text-xs text-muted-foreground">{reportDescription}</p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 mt-2">
          <Button
            type="button"
            onClick={onExportCsv}
            disabled={isExportingCsv || isExportCsvDisabled}
          >
            <Download aria-hidden="true" />
            {isExportingCsv ? "Exporting…" : exportCsvLabel}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onExportPdf}
            disabled={isExportPdfDisabled}
          >
            <FileText aria-hidden="true" />
            {exportPdfLabel}
          </Button>
        </div>
        {lockedMessage ? (
          <Alert>
            <AlertTitle>Upgrade required</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>{lockedMessage}</p>
            </AlertDescription>
            <AlertAction>
              <Button asChild size="sm" variant="outline">
                <Link to="/billing">
                  {getUpgradeLabel(lockedUpgradeTarget ?? "core")}
                </Link>
              </Button>
            </AlertAction>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SummarySection({ metrics }: { metrics: SummaryMetric[] }) {
  return <SummaryCards metrics={metrics} />;
}

function SiteBreakdownSection({
  rows,
  isLoading,
}: {
  rows: ManagerReportSiteBreakdownEntry[];
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Site breakdown</CardTitle>
        <CardDescription>
          Sign-in coverage across your managed sites for the selected period.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SiteBreakdownTable rows={rows} isLoading={isLoading} />
      </CardContent>
    </Card>
  );
}

function RecentSignInsSection({
  rows,
  isLoading,
}: {
  rows: ManagerReportRecentSignIn[];
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Recent sign-ins</CardTitle>
        <CardDescription>
          Latest 20 sign-ins for the selected filters, including flagged
          entries.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RecentSignInsTable rows={rows} isLoading={isLoading} />
      </CardContent>
    </Card>
  );
}

function ReportsInner() {
  const [selectedSiteId, setSelectedSiteId] = useState<string>("all");
  const [reportType, setReportType] = useState<ReportTypeOption["id"]>(
    REPORT_TYPES[0].id
  );
  const [selectedWorkerId, setSelectedWorkerId] = useState<string>("all");
  const [startDate, setStartDate] = useState<Date | undefined>(
    subDays(new Date(), 6)
  );
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());

  const normalizedStartDate = startDate
    ? format(startDate, "yyyy-MM-dd")
    : undefined;
  const normalizedEndDate = endDate ? format(endDate, "yyyy-MM-dd") : undefined;

  const resolvedSiteId =
    selectedSiteId === "all" ? undefined : selectedSiteId || undefined;
  const resolvedWorkerId =
    selectedWorkerId === "all" ? undefined : selectedWorkerId;
  const managedSites = useQuery(trpc.getManagedSites.queryOptions());
  const workers = useQuery(trpc.getWorkers.queryOptions());
  const reportingCapabilitiesQuery = useQuery({
    ...trpc.getReportingCapabilities.queryOptions({
      siteId: resolvedSiteId,
    }),
    placeholderData: (previousData) => previousData,
  });
  const capabilities =
    (reportingCapabilitiesQuery.data as ReportCapabilities | undefined) ?? null;
  const isAttendanceReport = reportType === "attendance";
  const isFormsReport = reportType === "forms";
  const isHistoryWindowLocked = capabilities
    ? exceedsHistoryWindow({
        historyWindowDays: capabilities.historyWindowDays,
        startDate,
        endDate,
      })
    : false;
  const canLoadAttendanceReport =
    Boolean(capabilities?.canViewAttendanceReports) && !isHistoryWindowLocked;
  const canLoadFormsReport =
    Boolean(capabilities?.canViewFormsReporting) && !isHistoryWindowLocked;

  const formsPreviewQuery = useQuery({
    ...trpc.getFormsPreview.queryOptions({
      siteId: resolvedSiteId,
      startDate: normalizedStartDate,
      endDate: normalizedEndDate,
      workerId: resolvedWorkerId,
    }),
    enabled: isFormsReport && canLoadFormsReport,
    placeholderData: (previousData) => previousData,
  });

  const reportQuery = useQuery({
    ...trpc.getManagerReport.queryOptions({
      siteId: resolvedSiteId,
      startDate: normalizedStartDate,
      endDate: normalizedEndDate,
      reportType,
    }),
    enabled: isAttendanceReport && canLoadAttendanceReport,
    placeholderData: (previousData) => previousData,
  });

  const exportCsvMutation = useMutation({
    ...trpc.exportWorkerSignIns.mutationOptions(),
    onSuccess: (data) => {
      if (!data.csv) {
        toast.info("No sign-ins found for the selected filters");
        return;
      }

      const blob = new Blob([data.csv], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = data.fileName ?? "worker-signins.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("CSV export ready");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const exportFormsMutation = useMutation({
    ...trpc.exportForms.mutationOptions(),
    onSuccess: (data) => {
      if (!data.csv) {
        toast.info("No form submissions found for the selected filters");
        return;
      }

      const blob = new Blob([data.csv], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = data.fileName ?? "worker-forms.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("CSV export ready");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleExportCsv = () => {
    if (reportType !== "forms" && !capabilities?.canExportCsv) {
      toast.error("Upgrade to Base to export CSV reports.");
      return;
    }

    if (isHistoryWindowLocked) {
      toast.error(
        getHistoryWindowMessage(capabilities?.historyWindowDays ?? 30)
      );
      return;
    }

    if (reportType === "forms") {
      if (!capabilities?.canExportForms) {
        toast.error("Upgrade to Core to export form submissions.");
        return;
      }

      exportFormsMutation.mutate({
        siteId: resolvedSiteId,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
        workerId: resolvedWorkerId,
      });
      return;
    }

    exportCsvMutation.mutate({
      siteId: resolvedSiteId,
      startDate: normalizedStartDate,
      endDate: normalizedEndDate,
      workerId: resolvedWorkerId,
    });
  };

  const handleExportPdf = () => {
    if (!capabilities?.canExportPdf) {
      toast.error("Upgrade to Core to export PDF reports.");
      return;
    }

    if (isHistoryWindowLocked) {
      toast.error(
        getHistoryWindowMessage(capabilities?.historyWindowDays ?? 30)
      );
      return;
    }

    const siteLabel = resolvedSiteId
      ? (managedSites.data?.find((site) => site.id === resolvedSiteId)?.name ??
        "Selected site")
      : "All managed sites";

    const reportTypeLabel =
      REPORT_TYPES.find((type) => type.id === reportType)?.label ??
      "Attendance overview";

    const generatedAtLabel = format(new Date(), "PPpp");
    const dateRangeLabel = `${formatDateLabel(startDate)} → ${formatDateLabel(endDate)}`;

    try {
      let pdfBytes: Uint8Array;

      if (reportType === "forms") {
        const data = formsPreviewQuery.data as
          | FormsReportPreviewResponse
          | undefined;
        if (!data) {
          toast.info("Generate a forms report first");
          return;
        }

        pdfBytes = createReportPdf({
          variant: "forms",
          report: data,
          siteLabel,
          reportTypeLabel,
          dateRangeLabel,
          generatedAtLabel,
        });
      } else {
        const data = reportQuery.data;
        if (!data) {
          toast.info("Generate a report first");
          return;
        }

        pdfBytes = createReportPdf({
          variant: "attendance",
          report: data,
          siteLabel,
          reportTypeLabel,
          dateRangeLabel,
          generatedAtLabel,
        });
      }

      const pdfPayload = new Uint8Array(pdfBytes.byteLength);
      pdfPayload.set(pdfBytes);
      const blob = new Blob([pdfPayload], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `site-manager-report-${format(new Date(), "yyyyMMdd-HHmm")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("PDF export ready");
    } catch (error) {
      console.error("Failed to create PDF", error);
      toast.error("Could not generate the PDF report");
    }
  };

  const summaryCards = useMemo<SummaryMetric[]>(
    () =>
      createSummaryMetrics({
        report: isHistoryWindowLocked ? undefined : reportQuery.data,
        startDate,
        endDate,
      }),
    [isHistoryWindowLocked, reportQuery.data, startDate, endDate]
  );

  const reportDescription = REPORT_TYPES.find(
    (type) => type.id === reportType
  )?.subtitle;

  const isExportingCsv =
    exportCsvMutation.isPending || exportFormsMutation.isPending;
  const formsPreviewData = isHistoryWindowLocked
    ? undefined
    : formsPreviewQuery.data;
  const reportData = isHistoryWindowLocked ? undefined : reportQuery.data;
  const exportCsvDisabled = isFormsReport
    ? !capabilities?.canExportForms || isHistoryWindowLocked
    : !capabilities?.canExportCsv || isHistoryWindowLocked;
  const exportPdfDisabled =
    !capabilities?.canExportPdf || isHistoryWindowLocked;
  const exportCsvLabel = isFormsReport
    ? capabilities === null || capabilities.canExportForms
      ? "Export CSV"
      : "Upgrade to export CSV"
    : "Export CSV";
  const exportPdfLabel =
    capabilities === null || capabilities.canExportPdf
      ? "Export PDF"
      : "Upgrade to export PDF";
  const lockedMessage =
    capabilities === null
      ? null
      : isHistoryWindowLocked
        ? getHistoryWindowMessage(capabilities.historyWindowDays ?? 30)
        : isFormsReport && !capabilities.canExportForms
          ? "Forms export is available on Core."
          : !isFormsReport && !capabilities.canExportCsv
            ? "CSV export is available on Base."
            : !capabilities.canExportPdf
              ? "PDF export is available on Core."
              : null;
  const lockedUpgradeTarget: UpgradeTarget | null =
    capabilities === null
      ? null
      : isHistoryWindowLocked
        ? "core"
        : isFormsReport && !capabilities.canExportForms
          ? "core"
          : !isFormsReport && !capabilities.canExportCsv
            ? "base"
            : !capabilities.canExportPdf
              ? "core"
              : null;

  const formsSummaryMetrics = useMemo<SummaryMetric[]>(() => {
    const data = formsPreviewData;
    if (!data) {
      return [
        { id: "total-forms", label: "Total submissions", value: "—" },
        { id: "pending-review", label: "Pending review", value: "—" },
        { id: "approved", label: "Approved", value: "—" },
        { id: "rejected", label: "Rejected", value: "—" },
      ];
    }

    return [
      {
        id: "total-forms",
        label: "Total submissions",
        value: numberFormatter.format(data.summary.totalSubmissions),
        subLabel: `${numberFormatter.format(data.summary.uniqueTemplates)} templates`,
      },
      {
        id: "pending-review",
        label: "Pending review",
        value: numberFormatter.format(data.summary.pendingReview),
        accent: data.summary.pendingReview > 0 ? "attention" : "default",
      },
      {
        id: "approved",
        label: "Approved",
        value: numberFormatter.format(data.summary.approved),
      },
      {
        id: "closed",
        label: "Closed",
        value: numberFormatter.format(data.summary.closed),
        subLabel: `${numberFormatter.format(data.summary.rejected)} rejected`,
        accent: data.summary.rejected > 0 ? "attention" : "default",
      },
    ];
  }, [formsPreviewData]);

  const formsPreviewList = formsPreviewData?.preview ?? [];

  return (
    <PageContainer className="flex min-h-full flex-col gap-6 lg:gap-8">
      <PageHeader
        title="Reports"
        description="Review attendance trends, site coverage, and exports across your managed sites."
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start">
        <aside>
          <FiltersSection
            selectedSiteId={selectedSiteId}
            onSiteChange={setSelectedSiteId}
            sites={managedSites.data ?? []}
            selectedWorkerId={selectedWorkerId}
            onWorkerChange={setSelectedWorkerId}
            workers={workers.data ?? []}
            isWorkersLoading={workers.isLoading}
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
            reportType={reportType}
            onReportTypeChange={setReportType}
            reportDescription={reportDescription}
            onExportCsv={handleExportCsv}
            onExportPdf={handleExportPdf}
            isExportingCsv={isExportingCsv}
            isExportCsvDisabled={exportCsvDisabled}
            isExportPdfDisabled={exportPdfDisabled}
            exportCsvLabel={exportCsvLabel}
            exportPdfLabel={exportPdfLabel}
            lockedMessage={lockedMessage}
            lockedUpgradeTarget={lockedUpgradeTarget}
            historyWindowDays={capabilities?.historyWindowDays ?? null}
          />
        </aside>

        <section className="min-w-0 space-y-6">
          {isHistoryWindowLocked ? (
            <Alert>
              <AlertTitle>History locked</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>
                  {getHistoryWindowMessage(
                    capabilities?.historyWindowDays ?? 30
                  )}
                </p>
              </AlertDescription>
              <AlertAction>
                <Button asChild size="sm" variant="outline">
                  <Link to="/billing">{getUpgradeLabel("core")}</Link>
                </Button>
              </AlertAction>
            </Alert>
          ) : isAttendanceReport ? (
            <>
              <SummarySection metrics={summaryCards} />

              <SiteBreakdownSection
                rows={
                  (reportData?.siteBreakdown as ManagerReportSiteBreakdownEntry[]) ??
                  []
                }
                isLoading={reportQuery.isLoading}
              />

              <RecentSignInsSection
                rows={
                  (reportData?.recentSignIns as ManagerReportRecentSignIn[]) ??
                  []
                }
                isLoading={reportQuery.isLoading}
              />
            </>
          ) : (
            <>
              <SummarySection metrics={formsSummaryMetrics} />

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle>Recent submissions</CardTitle>
                  <CardDescription>
                    Preview the latest forms matching your filters. Export for
                    the full dataset.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {formsPreviewQuery.isLoading ? (
                    <div className="text-muted-foreground">Loading forms…</div>
                  ) : formsPreviewList.length === 0 ? (
                    <div className="font-bold">No submitted forms found.</div>
                  ) : (
                    <ul className="divide-y rounded-md border bg-card">
                      {formsPreviewList.map((item) => {
                        const submittedLabel = item.submittedAt
                          ? format(new Date(item.submittedAt), "PPpp")
                          : "Unknown date";
                        const statusLabel = item.approvalStatus
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (char) => char.toUpperCase());
                        const submittedBy =
                          item.submittedByName ??
                          item.submittedByEmail ??
                          "Unknown worker";

                        return (
                          <li key={item.submissionId} className="p-4 space-y-1">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-medium">
                                  {item.templateName ?? "Untitled template"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {item.siteName}
                                </p>
                              </div>
                              <span className="text-xs font-medium uppercase text-muted-foreground">
                                {statusLabel}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Submitted by {submittedBy}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {submittedLabel}
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </section>
      </div>
    </PageContainer>
  );
}

export default function Reports() {
  return <ReportsInner />;
}
